// mapLayerRenderer.js - 处理地图图层渲染相关功能
import L from 'leaflet'
import 'leaflet-polylinedecorator'

/**
 * 计算两点间的距离（单位：米，使用Haversine公式）
 * @param {Array} point1 - 第一个点 [lat, lng]
 * @param {Array} point2 - 第二个点 [lat, lng]
 * @returns {number} 距离（米）
 */
function calculateDistanceInMeters(point1, point2) {
  const lat1 = point1[0] * Math.PI / 180; // 转换为弧度
  const lng1 = point1[1] * Math.PI / 180;
  const lat2 = point2[0] * Math.PI / 180;
  const lng2 = point2[1] * Math.PI / 180;
  
  const deltaLat = lat2 - lat1;
  const deltaLng = lng2 - lng1;
  
  const a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
            Math.cos(lat1) * Math.cos(lat2) *
            Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const R = 6371000; // 地球半径（米）
  
  return R * c;
}

// 存储船舶历史位置，格式: {mmsi: Array<{lat, lng, timestamp}>}
const shipTrackHistory = {};

// 存储船舶最后更新时间，格式: {mmsi: timestamp}
const shipLastUpdateTime = {};

/**
 * 清理过期的船舶数据（5分钟内没有收到新数据的船舶）
 * @param {Array} currentShips - 当前活跃的船舶数组
 * @returns {Array} 清理后的船舶数组
 */
export function cleanupExpiredShips(currentShips) {
  const now = Date.now();
  const EXPIRY_TIME = 5 * 60 * 1000; // 5分钟
  
  // 更新当前活跃船舶的最后更新时间
  currentShips.forEach(ship => {
    if (ship && ship.userid) {
      const mmsi = ship.userid;
      const updateTime = ship.receive_time ? new Date(ship.receive_time).getTime() : now;
      shipLastUpdateTime[mmsi] = updateTime;
    }
  });
  
  // 找出过期的MMSI（只检查已知的MMSI，避免重复清理）
  const expiredMMSI = [];
  Object.keys(shipLastUpdateTime).forEach(mmsi => {
    const lastUpdate = shipLastUpdateTime[mmsi];
    if (now - lastUpdate > EXPIRY_TIME) {
      expiredMMSI.push(mmsi);
    }
  });
  
  // 清理过期的船舶数据
  expiredMMSI.forEach(mmsi => {
    delete shipLastUpdateTime[mmsi];
    delete shipTrackHistory[mmsi];
  });
  
  // 过滤掉过期的船舶（从当前船舶数组中移除）
  const activeShips = currentShips.filter(ship => {
    if (!ship || !ship.userid) return false;
    return !expiredMMSI.includes(ship.userid);
  });
  
  return activeShips;
}

/**
 * 在船舶图标前方绘制一条方向线
 * @param {CanvasRenderingContext2D} ctx - Canvas上下文
 * @param {L.Point} point - 船舶在屏幕上的像素位置
 * @param {number} angle - 船舶角度（弧度制）
 * @param {number} length - 线段长度（像素）
 */
export function drawShipDirectionLine(ctx, point, angle, length = 8) {
  if (angle === undefined || angle === null) return;
  
  ctx.save();
  
  const iconLen = 16;

  //计算起点坐标
  const startX = point.x + (iconLen/2) * Math.sin(angle);
  const startY = point.y - (iconLen/2) * Math.cos(angle);
  
  // 计算终点坐标（基于角度和长度）
  const endX = point.x + (iconLen+length) * Math.sin(angle); // 注意：sin对应x轴，因为角度是基于北向顺时针
  const endY = point.y - (iconLen+length) * Math.cos(angle); // 注意：cos对应y轴，并且y轴向下为正，所以用负号
  
  // 绘制线段
  ctx.beginPath();
  ctx.moveTo(startX, startY);
  ctx.lineTo(endX, endY);
  ctx.strokeStyle = 'black';
  ctx.lineWidth = 2;
  ctx.stroke();
  
  ctx.restore();
}

/**
 * 获取地图当前比例尺（米）。
 * 读取 Leaflet 比例尺控件（.leaflet-control-scale-line）显示文本，如 "300 m"、"5 km"。
 * 解析失败时返回 0。
 * @param {L.Map} map - Leaflet地图对象
 * @returns {number} 当前比例尺对应的实际距离（米）
 */
function getMapScaleInMeters(map) {
  try {
    const container = map && map.getContainer && map.getContainer();
    const line = container && container.querySelector('.leaflet-control-scale-line');
    if (line && line.textContent) {
      const text = line.textContent.trim().toLowerCase(); // 如 "5 km" / "300 m"
      const m = text.match(/([\d.]+)\s*(km|m)/);
      if (m) {
        return parseFloat(m[1]) * (m[2] === 'km' ? 1000 : 1);
      }
    }
  } catch (e) {
    // 忽略解析异常，返回 0
  }
  return 0;
}

/**
 * 在Canvas上绘制船舶矢量图标（完全替代PNG图片图标，无需引入图片资源）
 * 形状还原原 ship-ais.png：顶点（船头）+ 底边中点向内拉拢（共4点），形成两尾翼效果
 * 普通船舶蓝色（与原图标一致），告警船舶红色并带闪烁脉动
 * 大比例尺模式（地图比例尺 >= 5KM）下，所有船舶图标改换为蓝色小圆点（告警船保持红色以保留辨识度）
 * 仅绘制当前渲染的船舶标记，由canvasmarker图层统一调度（性能与图片绘制同级）
 * @param {CanvasRenderingContext2D} ctx - Canvas上下文
 * @param {L.Point} point - 船舶图标中心点的屏幕像素坐标
 * @param {Number} angle - 船舶朝向角度（弧度制）
 * @param {Boolean} isAlert - 是否为告警船舶（告警变红+闪烁）
 * @param {Boolean} largeScale - 是否为大比例尺模式（图标显示为小圆点）
 */
function drawShipVectorIcon(ctx, point, angle, isAlert, largeScale) {
  // 告警船舶闪烁：约 0.8s 一个完整周期（alpha 0.65 ~ 1.0，始终清晰可辨）；普通船恒为 1
  let alpha = 1;
  if (isAlert) {
    const now = Date.now();
    const phase = (Math.sin(now / 400) + 1) / 2; // 0 ~ 1
    alpha = 0.65 + phase * 0.35; // 0.65 ~ 1.0
  }

  // 配色（与原图主体色一致：蓝色 58,94,234）
  const main = isAlert ? '#e63946' : '#3a5ede';

  ctx.save();
  ctx.globalAlpha = alpha;

  // 大比例尺模式：船舶图标改换为蓝色小圆点（告警船舶红色圆点，保持闪烁辨识）
  if (largeScale) {
    ctx.beginPath();
    ctx.arc(point.x, point.y, 3, 0, Math.PI * 2);
    ctx.fillStyle = main;
    ctx.fill();
    ctx.restore();
    return;
  }

  ctx.translate(point.x, point.y);
  ctx.rotate(angle || 0);

  // 主体：4点形状（顶点 + 左下翼尖 + 底边中点内缩 + 右下翼尖），24x24 图标范围
  ctx.beginPath();
  ctx.moveTo(0, -12);       // 顶点（船头）
  ctx.lineTo(-7, 5.5);      // 左下翼尖
  ctx.lineTo(0, 1.5);       // 底边中点（向内拉拢，形成两尾翼）
  ctx.lineTo(7, 5.5);       // 右下翼尖
  ctx.closePath();
  ctx.fillStyle = main;
  ctx.fill();

  ctx.restore();
}

/**
 * 绘制船舶航迹线
 * @param {CanvasRenderingContext2D} ctx - Canvas上下文
 * @param {L.Map} map - Leaflet地图对象
 * @param {String} mmsi - 船舶MMSI号
 * @param {L.Point} currentPoint - 船舶当前在屏幕上的像素位置
 * @param {Object} currentShip - 船舶当前信息对象，包含经纬度
 */
export function drawShipTrackLine(ctx, map, mmsi, currentPoint, currentShip) {
  if (!mmsi || !shipTrackHistory[mmsi] || shipTrackHistory[mmsi].length < 2) return;
  
  // console.warn('当前船舶信息',currentShip);
  
  const trackPoints = shipTrackHistory[mmsi];
  
  // 如果没有当前船舶位置信息，则使用历史记录中最新的点
  const currentLat = currentShip ? currentShip.pos.latitude : trackPoints[trackPoints.length - 1].lat;
  const currentLng = currentShip ? currentShip.pos.longitude : trackPoints[trackPoints.length - 1].lng;
  
  // 获取当前时间戳
  const currentTime = Date.now();
  
  // 应用三个筛选条件同时成立的点：
  // 1. 与当前位置距离不超过1000米（过滤明显跳点，同时支持更长的尾迹范围）
  // 2. 与当前最新的AIS数据不超过5分钟（300000毫秒）
  // 3. 最近的50个历史数据点
  const filteredTrackPoints = trackPoints
    .filter(point => {
      // 条件1：距离不超过1000米
      const distanceInMeters = calculateDistanceInMetersLatLng(
        currentLat, currentLng, point.lat, point.lng
      );
      if (distanceInMeters > 1000) return false;
      
      // 条件2：时间不超过5分钟
      const timeDiff = currentTime - (point.timestamp || 0);
      if (timeDiff > 300000) return false; // 300000ms = 5分钟
      
      return true;
    })
    .slice(-50); // 条件3：最多保留最近的50个点
  
  // 如果过滤后没有足够的点，则不绘制
  if (filteredTrackPoints.length < 2) return;

  ctx.save();
  ctx.beginPath();
  
  // 设置航迹线样式
  ctx.strokeStyle = 'black';
  ctx.lineWidth = 1;
  ctx.setLineDash([2, 2]); // 绘制虚线效果
  
  // 从当前位置开始绘制（即船舶后方）
  ctx.moveTo(currentPoint.x, currentPoint.y);
  
  // 绘制历史轨迹点
  for (let i = filteredTrackPoints.length - 1; i >= 0; i--) {
    const point = map.latLngToContainerPoint(L.latLng(filteredTrackPoints[i].lat, filteredTrackPoints[i].lng));
    ctx.lineTo(point.x, point.y);
  }
  
  // 绘制线段
  ctx.stroke();
  ctx.restore();
}

/**
 * 计算两个地理坐标点之间的距离（Haversine公式）
 * @param {Number} lat1 - 第一个点的纬度
 * @param {Number} lng1 - 第一个点的经度
 * @param {Number} lat2 - 第二个点的纬度
 * @param {Number} lng2 - 第二个点的经度
 * @returns {Number} 距离，单位为千米
 */
function calculateDistance(lat1, lng1, lat2, lng2) {
  const R = 6371; // 地球半径，单位为千米
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * 
            Math.sin(dLng / 2) * Math.sin(dLng / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;
  
  return distance;
}

/**
 * 计算两个地理坐标点之间的距离（Haversine公式）- 返回米为单位
 * @param {Number} lat1 - 第一个点的纬度
 * @param {Number} lng1 - 第一个点的经度
 * @param {Number} lat2 - 第二个点的纬度
 * @param {Number} lng2 - 第二个点的经度
 * @returns {Number} 距离，单位为米
 */
function calculateDistanceInMetersLatLng(lat1, lng1, lat2, lng2) {
  const R = 6371000; // 地球半径，单位为米
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * 
            Math.sin(dLng / 2) * Math.sin(dLng / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;
  
  return distance;
}

/**
 * 将角度转换为弧度
 * @param {Number} degrees - 角度值
 * @returns {Number} 弧度值
 */
function toRad(degrees) {
  return degrees * Math.PI / 180;
}

/**
 * 绘制船舶名称作为工具提示
 * @param {CanvasRenderingContext2D} ctx - Canvas上下文
 * @param {L.Point} point - 船舶在屏幕上的像素位置
 * @param {Object} iconOptions - 图标选项
 * @param {String} shipName - 船舶名称
 */
export function drawShipNameAsToolTip(ctx, point, iconOptions, shipName) {
  if (!shipName) return;
  
  const iconHeight = iconOptions.iconSize[1];
  const iconWidth = iconOptions.iconSize[0];
  
  // 设置文本样式
  ctx.save();
  ctx.font = '12px Arial';
  ctx.textAlign = 'left'; // 改为左对齐，因为文本要向右偏移
  ctx.textBaseline = 'top';
  
  // 计算文本宽度以确定背景矩形大小
  const textWidth = ctx.measureText(shipName).width;
  
  // 设置文本位置（向右上方偏移）
  const textX = point.x - textWidth/2 + 10; // 向右偏移
  const textY = point.y - iconHeight/2 - 20; // 向上偏移
  
  // 绘制半透明背景
  ctx.fillStyle = 'rgba(4, 124, 8, 0.7)';
  ctx.fillRect(
    textX - 3, 
    textY - 1, 
    textWidth + 6, 
    14
  );
  
  // 绘制文本
  ctx.fillStyle = 'white';
  ctx.fillText(shipName, textX, textY);
  
  // 绘制连接线
  ctx.beginPath();
  ctx.moveTo(point.x, point.y); // 从船舶中心点开始
  ctx.lineTo(textX + textWidth/2, textY + 14); // 到文本底部中心点
  ctx.strokeStyle = 'rgba(4, 124, 8, 0.9)';
  ctx.lineWidth = 1;
  ctx.stroke();
  
  ctx.restore();
}

/**
 * 初始化地图图层配置
 * @param {string} mapType 地图类型
 * @param {string} tiandituKey 天地图API密钥
 * @param {boolean} showLabels 是否显示标注
 * @returns {Array} 图层配置数组
 */
export function initLayers(mapType, tiandituKey, showLabels = true) {
  const layers = []
  
  // 配置图层
  switch(mapType) {
    case 'normal':
      // 矢量图层
      layers.push({
        name: 'TianDiTu.Normal.Map',
        url: `https://t{s}.tianditu.gov.cn/DataServer?T=vec_w&X={x}&Y={y}&L={z}&tk=${tiandituKey}`,
        attribution: '&copy; 天地图',
        subdomains: ['0', '1', '2', '3', '4', '5', '6', '7'],
        layerType: 'base',
        visible: true,
        options: {
          maxZoom: 18,
          minZoom: 5
        }
      })
      // 注记图层
      if (showLabels) {
        layers.push({
          name: 'TianDiTu.Normal.Annotion',
          url: `https://t{s}.tianditu.gov.cn/DataServer?T=cva_w&X={x}&Y={y}&L={z}&tk=${tiandituKey}`,
          attribution: '&copy; 天地图',
          subdomains: ['0', '1', '2', '3', '4', '5', '6', '7'],
          layerType: 'overlay',
          visible: true,
          options: {
            maxZoom: 18,
            minZoom: 5
          }
        })
      }
      break
    case 'satellite':
      // 卫星影像
      layers.push({
        name: 'TianDiTu.Satellite.Map',
        url: `https://t{s}.tianditu.gov.cn/DataServer?T=img_w&X={x}&Y={y}&L={z}&tk=${tiandituKey}`,
        attribution: '&copy; 天地图',
        subdomains: ['0', '1', '2', '3', '4', '5', '6', '7'],
        layerType: 'base',
        visible: true,
        options: {
          maxZoom: 18,
          minZoom: 5
        }
      })
      // 卫星影像注记
      if (showLabels) {
        layers.push({
          name: 'TianDiTu.Satellite.Annotion',
          url: `https://t{s}.tianditu.gov.cn/DataServer?T=cia_w&X={x}&Y={y}&L={z}&tk=${tiandituKey}`,
          attribution: '&copy; 天地图',
          subdomains: ['0', '1', '2', '3', '4', '5', '6', '7'],
          layerType: 'overlay',
          visible: true,
          options: {
            maxZoom: 18,
            minZoom: 5
          }
        })
      }
      break
    case 'terrain':
      // 地形图
      layers.push({
        name: 'TianDiTu.Terrain.Map',
        url: `https://t{s}.tianditu.gov.cn/DataServer?T=ter_w&X={x}&Y={y}&L={z}&tk=${tiandituKey}`,
        attribution: '&copy; 天地图',
        subdomains: ['0', '1', '2', '3', '4', '5', '6', '7'],
        layerType: 'base',
        visible: true,
        options: {
          maxZoom: 18,
          minZoom: 5
        }
      })
      // 地形图注记
      if (showLabels) {
        layers.push({
          name: 'TianDiTu.Terrain.Annotion',
          url: `https://t{s}.tianditu.gov.cn/DataServer?T=cta_w&X={x}&Y={y}&L={z}&tk=${tiandituKey}`,
          attribution: '&copy; 天地图',
          subdomains: ['0', '1', '2', '3', '4', '5', '6', '7'],
          layerType: 'overlay',
          visible: true,
          options: {
            maxZoom: 18,
            minZoom: 5
          }
        })
      }
      break
  }
  
  return layers
}

/**
 * 创建船舶航迹图层
 * @param {Object} map Leaflet地图实例
 * @param {Array} shipTracks 船舶航迹数据
 * @param {Function} onTrackClick 点击航迹的回调函数
 * @returns {Object} 船舶航迹图层组
 */
export function createShipTracksLayer(map, shipTracks, onTrackClick) {
  if (!map || !shipTracks || shipTracks.length === 0) {
    console.warn('无法创建船舶航迹线图层：地图未准备好或没有航迹数据')
    return null
  }
  
  // 创建新的图层组
  const layerGroup = L.layerGroup()
  
  // 为每条航迹创建线段
  shipTracks.forEach(track => {
    try {
      // 创建带箭头的线段
      const polyline = L.polyline(track.points, {
        color: track.color || '#1E90FF',
        weight: track.width || 3,
        opacity: 0.8
      })
      
      // 添加箭头装饰器
      const arrowDecorator = L.polylineDecorator(polyline, {
        patterns: [
          {
            offset: '60%', // 箭头位置
            repeat: 0, // 不重复
            symbol: L.Symbol.arrowHead({
              pixelSize: 15, // 箭头大小
              headAngle: 60, // 箭头角度
              polygon: true, // 使用多边形
              pathOptions: { // 箭头样式
                color: track.color || '#1E90FF',
                fillOpacity: 1,
                weight: 0
              }
            })
          }
        ]
      })
      
      // 添加到图层组
      polyline.addTo(layerGroup)
      arrowDecorator.addTo(layerGroup)
      
      // // 添加提示信息
      // polyline.bindTooltip(track.shipName + ' 的航迹')
      
      // // 添加点击事件
      // if (typeof onTrackClick === 'function') {
      //   polyline.on('click', () => {
      //     onTrackClick(track)
      //   })
      // }
    } catch (e) {
      console.error('创建航迹线失败:', e, track)
    }
  })
  
  console.log('船舶航迹线图层创建完成')
  return layerGroup
}

/**
 * 创建警戒区域图层
 * @param {Object} map Leaflet地图实例
 * @param {Array} warningAreas 警戒区域数据
 * @param {Function} onAreaClick 点击区域的回调函数
 * @returns {Object} 警戒区域图层组
 */
export function createWarningAreasLayer(map, warningAreas, onAreaClick) {
  if (!map || !warningAreas || warningAreas.length === 0) {
    console.warn('无法创建警戒区域图层：地图未准备好或没有区域数据')
    return null
  }
  
  // 创建新的图层组，但不自动添加到地图
  const layerGroup = L.layerGroup()
  
  // 为每个警戒区域创建多边形
  warningAreas.forEach(area => {
    try {
      if (!area.points || area.points.length < 2) {
        console.warn('区域点数不足，无法创建区域图形:', area.name || '未命名区域');
        return;
      }
      
      let polygon = null;
      
      // 🎯 处理圆形类型
      if (area.geometryType === 'CIRCLE' && area.points.length >= 2) {
        const center = area.points[0]; // 圆心坐标
        const edge = area.points[1]; // 边界点坐标
        
        // 计算半径（使用Haversine公式计算地理距离）
        const radius = calculateDistanceInMeters(center, edge);
        
        // 创建圆形
        polygon = L.circle(center, {
          radius: radius, // 半径（米）
          color: area.borderColor || '#FF0000',
          weight: area.borderWidth || 2,
          fillColor: area.color || '#FF000080',
          fillOpacity: 0.2
        });
        
        console.log(`创建圆形区域: 中心[${center}], 半径${radius}米`);
      }
      // 处理线段类型（2个点）
      else if(area.points.length === 2 && area.geometryType !== 'CIRCLE'){
        polygon = L.polyline(area.points, {
          color: area.borderColor || '#FF0000',
          weight: area.borderWidth || 4
        })
      }
      // 处理多边形类型（>2个点）
      else{
        polygon = L.polygon(area.points, {
          color: area.borderColor || '#FF0000',
          weight: area.borderWidth || 2,
          fillColor: area.color || '#FF000080',
          fillOpacity: 0.2
        })
      }
      
      // 添加到图层组
      polygon.addTo(layerGroup)
      
      // 添加区域名称标签 - 仅为非绘制中的区域添加标签
      // 绘制中区域通常没有ID，或有特定的isDrawing标记
      if (area.name && !area.isDrawing && area.id) {
        // 使用与BridgeMap.vue相同的tooltip实现
        const tooltipOptions = { 
          permanent: true, 
          direction: 'center', 
          className: 'area-label', 
          interactive: false 
        }
        
        polygon.bindTooltip(area.name, tooltipOptions)
      }
      
      // 添加点击事件
      if (typeof onAreaClick === 'function') {
        polygon.on('click', (e) => {
          // 阻止事件传播
          L.DomEvent.stopPropagation(e);
          onAreaClick(area)
        })
      }
    } catch (e) {
      console.error('创建警戒区域失败:', e, area)
    }
  })
  
  console.log('警戒区域图层创建完成，包含', warningAreas.length, '个区域')
  return layerGroup
}

/**
 * 创建船舶Canvas图层
 * @param {L.Map} map Leaflet地图实例
 * @param {Object} shipIcon 船舶图标对象
 * @param {Array} ships 船舶数据数组
 * @param {Function} onShipClick 点击船舶时的回调函数
 * @returns {L.CanvasMarkerLayer} Canvas标记图层
 */
export function createShipCanvasLayer(map, shipIcon, ships, onShipClick) {
  if (!map) {
    console.error('无法创建Canvas图层：地图未初始化');
    return null;
  }
  
  try {
    console.log('开始创建船舶Canvas图层');
    
    // 创建Canvas图层
    const canvasLayer = L.canvasMarkerLayer({
      collisionFlg: false, // 关闭碰撞检测：船舶密集时所有船都绘制且可点击（碰撞检测会排除被遮挡的船，导致其无法点击）
      zIndex: 300, // 确保图层在最上层
      showText: false // 不显示文本，避免在地图上显示船舶名称
    });
    
    // 添加到地图
    canvasLayer.addTo(map);
    
    // 添加自定义事件处理机制
    // 点击事件
    canvasLayer.addOnClickListener((e, data) => {
      if(!data || data.length === 0){
        return;
      }
      const shipData = data[0].data && data[0].data.shipData;
      // console.warn('shipData',shipData);

      if(!shipData){
        return;
      }

      // 添加防抖处理
      if(shipData.shipClick){
        return;
      }
      shipData.shipClick = true;
      setTimeout(() => {
        shipData.shipClick = false;
      }, 500);

      console.log('点击船舶:', shipData.userid);          
      // 标记这是船舶点击事件，避免地图点击事件处理冲突
      e.originalEvent._shipClick = true;
      
      // 触发自定义事件
      if (map) {
        // 使用Leaflet的事件系统触发自定义事件
        map.fire('ship-click', {
          ship: shipData,
          originalEvent: e,
          latlng: shipData.pos ? L.latLng(shipData.pos.latitude, shipData.pos.longitude) : null
        });
      }
      
      // 如果提供了回调函数，也调用它（向后兼容）
      if (typeof onShipClick === 'function') {
        onShipClick(shipData);
      }
    });
    
    // 鼠标悬停事件
    canvasLayer.addOnHoverListener((e, data) => {
      if (data && data.length > 0) {
        const shipData = data[0].data && data[0].data.shipData;
        if (shipData) {
          // 触发自定义事件
          if (map) {
            map.fire('ship-mouseover', {
              ship: shipData,
              originalEvent: e,
              latlng: shipData.pos ? L.latLng(shipData.pos.latitude, shipData.pos.longitude) : null
            });
          }
        }
      } else {
        // 当鼠标不在任何船舶上时，触发mouse-leave事件
        if (map && canvasLayer._lastHoverShip) {
          map.fire('ship-mouseout', {
            ship: canvasLayer._lastHoverShip,
            originalEvent: e
          });
          canvasLayer._lastHoverShip = null;
        }
      }
      
      // 保存当前悬停的船舶，用于后续判断鼠标离开
      if (data && data.length > 0 && data[0].data && data[0].data.shipData) {
        canvasLayer._lastHoverShip = data[0].data.shipData;
      }
    });
    
    // 如果提供了船舶数据，直接添加到图层
    if (Array.isArray(ships) && ships.length > 0 && shipIcon) {
      try {
        console.log('在创建Canvas图层时添加船舶标记，数量:', ships.length);
        if(ships.length>0){
          console.log('首个船舶数据:', ships[0]);
        }
        
        // 创建并添加船舶标记
        const shipMarkers = ships
          .filter(ship => ship && ship.pos && ship.pos.latitude && ship.pos.longitude)
          .map(ship => {
            // 计算角度 - 船首向数据是基于正北方向顺时针360度范围的
            let angle = 0;
            
            // 优先使用heading（船首向），但需检查是否为有效值（AIS规范中511表示无效）
            if (ship.heading !== undefined && ship.heading !== null) {
              const heading = parseFloat(ship.heading);
              // 检查heading是否有效（不等于511且在0-359范围内）
              if (!isNaN(heading) && heading !== 511 && heading >= 0 && heading < 360) {
                angle = heading;
              } else if (ship.cog !== undefined && ship.cog !== null) {
                // 如果heading无效，使用cog（对地航向）
                angle = parseFloat(ship.cog);
              }
            } else if (ship.cog !== undefined && ship.cog !== null) {
              // 如果没有heading，使用cog（对地航向）
              angle = parseFloat(ship.cog);
            } else if (ship.course !== undefined && ship.course !== null) {
              // 如果没有heading和cog，尝试使用course（雷达数据可能使用course）
              angle = parseFloat(ship.course);
            }
            
            // 确保角度在0-360范围内
            angle = (angle + 360) % 360;
            
            // 将角度转换为弧度 (Canvas旋转API使用弧度)
            const angleInRadians = (Math.PI / 180) * angle;
            
            // 创建标记
            const marker = L.marker([ship.pos.latitude, ship.pos.longitude], {
              icon: L.icon({
                iconUrl: shipIcon.options.iconUrl,
                iconSize: shipIcon.options.iconSize,
                iconAnchor: shipIcon.options.iconAnchor,
                className: 'ship-icon',
                rotate: angleInRadians
              })
            });
            
            // 存储船舶数据
            marker.shipData = ship;
            // 存储角度信息，用于Canvas渲染
            marker.angle = angleInRadians;
            
            return marker;
          });
        
        // 批量添加到Canvas图层
        if (shipMarkers.length > 0) {
          canvasLayer.addLayers(shipMarkers);
          console.log('成功添加', shipMarkers.length, '个船舶标记到新创建的Canvas图层');
        }
      } catch (shipErr) {
        console.warn('创建Canvas图层时添加船舶标记失败:', shipErr);
        // 不因为添加船舶失败而中断
      }
    }
    
    console.log('Canvas图层初始化完成');
    return canvasLayer;
  } catch (err) {
    console.error('Canvas图层初始化错误:', err);
    return null;
  }
}

/**
 * 更新船舶标记
 * @param {L.CanvasMarkerLayer} canvasLayer Canvas图层
 * @param {Array} ships 船舶数据数组
 * @param {Object} shipIcon 船舶图标对象
 * @param {Object} bridgeCenter 桥梁中心点坐标 {lat, lng}
 * @param {number} displayRadius 显示半径（公里）
 * @param {Set} alertMmsiSet 当前存在未处理告警的MMSI集合（可选）
 * @returns {Array} 创建的船舶标记数组
 */
export function updateShipMarkers(canvasLayer, ships, shipIcon, bridgeCenter = null, displayRadius = 10, alertMmsiSet = null) {
  if (!canvasLayer || !Array.isArray(ships) || !shipIcon) {
    console.error('更新船舶标记参数无效');
    return [];
  }
  
  try {
    // 首先执行船舶出清，移除5分钟内没有收到新数据的船舶
    const activeShips = cleanupExpiredShips(ships);
    
    // 更新船舶历史位置缓存
    activeShips.forEach(ship => {
      if (ship && ship.userid && ship.pos && ship.pos.latitude && ship.pos.longitude) {
        const mmsi = ship.userid;
        const position = {
          lat: ship.pos.latitude,
          lng: ship.pos.longitude,
          timestamp: ship.receive_time ? new Date(ship.receive_time).getTime() : Date.now()
        };
        
        // 如果该船舶没有历史记录，则创建一个新数组
        if (!shipTrackHistory[mmsi]) {
          shipTrackHistory[mmsi] = [];
        }
        
        // 检查是否与最后一个位置相同，避免重复记录相同位置
        const lastPos = shipTrackHistory[mmsi].length > 0 ? 
          shipTrackHistory[mmsi][shipTrackHistory[mmsi].length - 1] : null;
        
        if (!lastPos || 
            lastPos.lat !== position.lat || 
            lastPos.lng !== position.lng) {
          // 添加当前位置到历史记录
          shipTrackHistory[mmsi].push(position);
          
          // 清理过期的历史记录（超过5分钟的记录）
          const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
          shipTrackHistory[mmsi] = shipTrackHistory[mmsi].filter(point => 
            (point.timestamp || 0) > fiveMinutesAgo
          );
          
          // 限制最大记录数量，保留最近的50个点（比之前的10个点更多，因为有时间筛选）
          if (shipTrackHistory[mmsi].length > 50) {
            shipTrackHistory[mmsi] = shipTrackHistory[mmsi].slice(-50);
          }
        }
      }
    });
    
  // 创建船舶标记 - 先过滤有效位置的船舶
  let filteredShips = activeShips.filter(ship => ship && ship.pos && ship.pos.latitude && ship.pos.longitude);

  // 将告警MMSI集合挂载到Canvas图层，供_overlay重绘扩展绘制红色闪烁高亮
  canvasLayer._alertMmsiSet = alertMmsiSet || new Set();
  
  // 距离过滤（已由服务端按桥梁范围过滤，保留代码供调试）
  if (bridgeCenter && bridgeCenter.lat && bridgeCenter.lng) {
    filteredShips = filteredShips.filter(ship => {
      const shipPoint = [ship.pos.latitude, ship.pos.longitude];
      const bridgePoint = [bridgeCenter.lat, bridgeCenter.lng];
      const distanceInMeters = calculateDistanceInMeters(shipPoint, bridgePoint);
      const distanceInKm = distanceInMeters / 1000;
      return distanceInKm <= displayRadius;
    });
    // console.log(`船舶距离过滤: 原始数量 ${ships.length}, 出清后 ${activeShips.length}, 有效位置 ${activeShips.filter(ship => ship && ship.pos && ship.pos.latitude && ship.pos.longitude).length}, 半径内 ${filteredShips.length}`);
  }
    
    const shipMarkers = filteredShips
      .map(ship => {
        try {
          // 计算角度 - 船首向数据是基于正北方向顺时针360度范围的
          let angle = 0;
          
          // 优先使用heading（船首向），但需检查是否为有效值（AIS规范中511表示无效）
          if (ship.heading !== undefined && ship.heading !== null) {
            const heading = parseFloat(ship.heading);
            // 检查heading是否有效（不等于511且在0-359范围内）
            if (!isNaN(heading) && heading !== 511 && heading >= 0 && heading < 360) {
              angle = heading;
            } else if (ship.cog !== undefined && ship.cog !== null) {
              // 如果heading无效，使用cog（对地航向）
              angle = parseFloat(ship.cog);
            }
          } else if (ship.cog !== undefined && ship.cog !== null) {
            // 如果没有heading，使用cog（对地航向）
            angle = parseFloat(ship.cog);
          } else if (ship.course !== undefined && ship.course !== null) {
            // 如果没有heading和cog，尝试使用course（雷达数据可能使用course）
            angle = parseFloat(ship.course);
          }
          
          // 确保角度在0-360范围内
          angle = (angle + 360) % 360;
          
          // 将角度转换为弧度 (Canvas旋转API使用弧度)
          const angleInRadians = (Math.PI / 180) * angle;
          
          // 可以添加调试日志查看角度变换
          // console.log("角度(度):", angle, "角度(弧度):", angleInRadians);
          
          // 获取船舶显示名称 - 优先显示中文名，其次英文名，最后MMSI
          // 支持多种数据源：AIS实时数据(vesselCnName/vesselName)和API详情数据(cnName/vesselName)
          const shipDisplayName = ship.cnName || ship.vesselCnName || 
                                  ship.shipname || ship.vesselName || ship.name || 
                                  ship.mmsi || ship.userid || '';
          
          // 判断该船舶是否处于告警状态
          const isAlert = alertMmsiSet && ship.userid && alertMmsiSet.has(ship.userid);
          const shipClassName = isAlert ? 'ship-icon ship-icon-alert' : 'ship-icon';

          // 创建船舶标记
          const marker = L.marker(
            [ship.pos.latitude, ship.pos.longitude], 
            {
              icon: L.icon({
                iconUrl: shipIcon.options.iconUrl,
                iconSize: shipIcon.options.iconSize,
                iconAnchor: shipIcon.options.iconAnchor,
                className: shipClassName,
                // 设置旋转角度(弧度制)
                rotate: angleInRadians
              })
            }
          );
          
          // 存储船舶数据到标记上
          marker.mmsi = ship.userid;
          marker.shipData = ship;
          // 存储角度信息，用于Canvas渲染 (弧度制)
          marker.angle = angleInRadians;
          // 存储船舶显示名称
          marker.shipDisplayName = shipDisplayName;
          
          return marker;
        } catch (e) {
          console.error('创建船舶标记失败:', e, ship);
          return null;
        }
      })
      .filter(Boolean); // 过滤掉创建失败的标记
    
    // 添加到Canvas图层
    if (shipMarkers.length > 0) {
      // console.log('添加船舶标记到Canvas图层，数量:', shipMarkers.length);
      try {
        // 根据船舶数量动态降级叠加绘制，优先保证地图流畅度
        const markerCount = shipMarkers.length;
        canvasLayer._shipRenderOptions = {
          drawDirection: markerCount <= 500,
          drawTrack: markerCount <= 220,
          drawName: markerCount <= 120
        };
        canvasLayer._shipOverlayMarkers = shipMarkers;

        // 仅首次安装重绘扩展，避免每次更新都重写_redraw
        if (!canvasLayer._shipOverlayRedrawInstalled && canvasLayer._redraw) {
          canvasLayer._shipOverlayRedrawInstalled = true;
          canvasLayer._originalRedraw = canvasLayer._redraw;

          // 覆盖_drawImage：所有船舶均用Canvas矢量绘制图标（不再加载PNG图片），告警船舶变红并闪烁
          if (!canvasLayer._shipVectorDrawInstalled && canvasLayer._drawImage) {
            canvasLayer._shipVectorDrawInstalled = true;
            canvasLayer._drawImage = function(marker, pointPos) {
              const alertMmsiSet = this._alertMmsiSet;
              const isAlert = !!(alertMmsiSet && marker && marker.mmsi && alertMmsiSet.has(marker.mmsi));
              // 普通船舶蓝色、告警船舶红色（闪烁），形状一致；
              // 大比例尺模式（地图比例尺 >= 5KM）下统一改为小圆点
              drawShipVectorIcon(this._ctx, pointPos, marker.angle, isAlert, this._largeScaleMode);
            };
          }

          canvasLayer._redraw = function(clear) {
            // 每次重绘前重新评估大比例尺模式（读取地图比例尺控件文本，>= 5KM 时船舶显示为蓝色小圆点）
            this._largeScaleMode = getMapScaleInMeters(this._map) >= 5000;
            // 先执行原始绘制（内部对告警船调用覆盖后的_drawImage，绘制红色矢量图标）
            const result = this._originalRedraw.call(this, clear);

            try {
              if (!this._ctx || !this._map) return result;
              const ctx = this._ctx;
              const map = this._map;
              const options = this._shipRenderOptions || {};
              const markers = this._shipOverlayMarkers || [];
              // 与库绘制图标使用同一视野判定（map.getBounds()），
              // 保证“图标隐藏时方向线/轨迹/船名等叠加信息也同步隐藏”
              const mapBounds = map.getBounds();

              markers.forEach(marker => {
                if (!marker || !marker.getLatLng) return;

                const latlng = marker.getLatLng();
                // 图标不可见（视野外）时，叠加信息同样不绘制，避免缩放/平移后残留
                if (!mapBounds.contains(latlng)) return;

                const point = map.latLngToContainerPoint(latlng);
                const iconOptions = marker.options && marker.options.icon && marker.options.icon.options;
                if (!iconOptions) return;

                // 大比例尺模式（圆点）下不绘制船舶尾迹线
                if (options.drawTrack && marker.mmsi && !this._largeScaleMode) {
                  drawShipTrackLine(ctx, map, marker.mmsi, point, marker.shipData);
                }

                // 大比例尺模式（圆点）下不绘制黑色方向线，避免短线穿过圆点造成干扰
                if (options.drawDirection && !this._largeScaleMode) {
                  drawShipDirectionLine(ctx, point, marker.angle);
                }

                // 大比例尺模式（圆点）下不绘制船名Label，避免大面积文字遮挡地图
                if (options.drawName && marker.shipDisplayName && !this._largeScaleMode) {
                  drawShipNameAsToolTip(ctx, point, iconOptions, marker.shipDisplayName);
                }
              });
            } catch (error) {
              console.error('绘制船舶叠加信息时出错:', error);
            }

            return result;
          };
        }
        
        // 添加船舶标记
        // 必须先清除旧标记：canvasmarker 库的 addLayer 不做去重，
        // 不清理会导致 rtree 中旧 marker 无限堆积，出现残留图标/重影/点击异常
        canvasLayer.clearLayers();
        canvasLayer.addLayers(shipMarkers);
        
        // 强制重绘Canvas图层
        if (canvasLayer._redraw) {
          canvasLayer._redraw(true);
        }
      } catch (addError) {
        console.error('添加船舶标记到Canvas图层失败:', addError);
      }
    } else {
      // console.warn('没有有效的船舶标记可添加');
    }
    
    return shipMarkers;
  } catch (err) {
    console.error('更新船舶标记错误:', err);
    return [];
  }
}

/**
 * 创建船舶图标
 * 图标不再依赖PNG图片资源：Canvas矢量绘制（drawShipVectorIcon）完全替代图片渲染，
 * iconUrl仅作为占位（1x1透明图），保证canvasmarker库内部逻辑正常
 * @param {string} [iconUrl] 图标URL（可选，默认1x1透明图）
 * @returns {Object} Leaflet图标对象
 */
export function createShipIcon(iconUrl) {
  const transparent = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==';
  return L.icon({
    iconUrl: iconUrl || transparent,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    className: 'ship-icon'
  })
}

/**
 * 更新图层可见性
 * @param {Object} map Leaflet地图实例
 * @param {Object} layerGroups 包含各种图层组的对象
 * @param {Object} layerStatus 图层状态对象
 */
export function updateLayerVisibility(map, layerGroups, layerStatus) {
  // 更新船舶航迹线图层显示状态
  if (layerGroups.shipTracksLayerGroup) {
    if (layerStatus.shipTracks) {
      if (!map.hasLayer(layerGroups.shipTracksLayerGroup)) {
        map.addLayer(layerGroups.shipTracksLayerGroup);
        // 保持图层层级
        layerGroups.shipTracksLayerGroup.setZIndex(200);
      }
    } else {
      if (map.hasLayer(layerGroups.shipTracksLayerGroup)) {
        map.removeLayer(layerGroups.shipTracksLayerGroup);
      }
    }
  }
  
  // 更新警戒区域图层显示状态
  if (layerGroups.warningAreasLayerGroup) {
    if (layerStatus.warningAreas) {
      if (!map.hasLayer(layerGroups.warningAreasLayerGroup)) {
        map.addLayer(layerGroups.warningAreasLayerGroup);
        // 保持图层层级
        layerGroups.warningAreasLayerGroup.setZIndex(100);
      }
    } else {
      if (map.hasLayer(layerGroups.warningAreasLayerGroup)) {
        map.removeLayer(layerGroups.warningAreasLayerGroup);
      }
    }
  }
  
  // 处理其他图层（桥梁、雷达等），如果已经添加了这些图层
  const otherLayerMap = {
    bridge: 'bridgeLayerGroup',
    bridgeReport: 'bridgeReportLayerGroup',
    bridgeChannel: 'bridgeChannelLayerGroup',
    bridgeNavigation: 'bridgeNavigationLayerGroup',
    bridgeWarn: 'bridgeWarnLayerGroup',
    bridgeForbid: 'bridgeForbidLayerGroup',
    radar: 'radarLayerGroup',
    cctv: 'cctvLayerGroup',
    radarTarget: 'radarTargetLayerGroup',
    leishenTarget: 'leishenTargetLayerGroup'
  };
  
  // 遍历其他图层，如果图层组存在则更新可见性
  Object.entries(otherLayerMap).forEach(([statusKey, groupKey]) => {
    if (layerGroups[groupKey]) {
      if (layerStatus[statusKey]) {
        if (!map.hasLayer(layerGroups[groupKey])) {
          map.addLayer(layerGroups[groupKey])
        }
      } else {
        if (map.hasLayer(layerGroups[groupKey])) {
          map.removeLayer(layerGroups[groupKey])
        }
      }
    }
  });
}

/**
 * 创建雷达图标
 * @param {string} iconUrl 图标URL
 * @returns {Object} Leaflet图标对象
 */
export function createRadarIcon(iconUrl) {
  return L.icon({
    iconUrl,
    iconSize: [16, 16],
    iconAnchor: [12, 12],
    className: 'radar-icon'
  })
}

/**
 * 创建雷达目标图层
 * @param {Object} map Leaflet地图实例
 * @param {Object} radarIcon 雷达图标
 * @param {Array} radarTargets 雷达目标数据数组
 * @param {Function} onRadarClick 点击雷达目标的回调函数
 * @returns {Object} Canvas图层对象
 */
export function createRadarTargetLayer(map, radarIcon, radarTargets, onRadarClick) {
  if (!map || !radarIcon) {
    console.error('无法初始化雷达目标图层：地图或图标未准备好');
    return null;
  }
  
  // 检查地图是否初始化完成
  if (!map._container || !map.getContainer()) {
    console.error('地图容器未初始化或不可用');
    return null;
  }
  
  // 检查地图panes是否已创建
  if (!map._panes || !map._panes.overlayPane) {
    console.error('地图panes未初始化，无法添加图层');
    return null;
  }
  
  try {
    console.log('初始化雷达目标Canvas图层');
    
    // 使用新的安全配置创建Canvas图层
    const canvasLayerOptions = {
      collisionFlg: false,  // 关闭碰撞检测，避免潜在问题
      field: "radarData",   // 指定存储数据的字段名
      duration: 0,          // 动画持续时间，0表示没有动画
      showText: false,      // 不显示文本
      zIndex: 290,          // 设置z-index确保在船舶图层下方显示
      opacity: 1            // 默认完全不透明
    };
    
    // 创建Canvas图层并添加到地图
    let canvasLayer = null;
    
    try {
      // 先创建图层但不添加到地图
      canvasLayer = L.canvasMarkerLayer(canvasLayerOptions);
      
      // 安全地添加到地图
      canvasLayer.addTo(map);
      
      console.log('雷达目标Canvas图层成功添加到地图');
    } catch (addErr) {
      console.error('添加雷达目标Canvas图层到地图失败:', addErr);
      return null;
    }
    
    // 添加点击事件
    if (typeof onRadarClick === 'function') {
      try {
        canvasLayer.addOnClickListener((e, data) => {
          if (data && data.length > 0) {
            const radarData = data[0].data && data[0].data.radarData;
            if (radarData) {
              console.log('点击雷达目标:', radarData.targetId);
              onRadarClick(radarData);
            } else {
              console.warn('点击雷达目标数据不完整:', data[0]);
            }
          }
        });
      } catch (eventErr) {
        console.warn('添加雷达目标Canvas图层点击事件失败:', eventErr);
        // 继续使用图层，不因事件添加失败而中断
      }
    }
    
    // 如果提供了雷达目标数据，直接添加到图层
    if (Array.isArray(radarTargets) && radarTargets.length > 0 && radarIcon) {
      try {
        console.log('在创建雷达目标Canvas图层时添加标记，数量:', radarTargets.length);
        
        // 创建并添加雷达目标标记
        const radarMarkers = radarTargets
          .filter(target => target && target.pos && target.pos.latitude && target.pos.longitude)
          .map(target => {
            // 创建标记 - 不旋转雷达图标
            const marker = L.marker([target.pos.latitude, target.pos.longitude], {
              icon: L.icon({
                iconUrl: radarIcon.options.iconUrl,
                iconSize: radarIcon.options.iconSize,
                iconAnchor: radarIcon.options.iconAnchor,
                className: 'radar-icon'
              })
            });
            
            // 存储雷达目标数据
            marker.radarData = target;
            
            return marker;
          });
        
        // 批量添加到Canvas图层
        if (radarMarkers.length > 0) {
          canvasLayer.addLayers(radarMarkers);
          console.log('成功添加', radarMarkers.length, '个雷达目标标记到新创建的Canvas图层');
        }
      } catch (targetErr) {
        console.warn('创建雷达目标Canvas图层时添加标记失败:', targetErr);
        // 不因为添加雷达目标失败而中断
      }
    }
    
    console.log('雷达目标Canvas图层初始化完成');
    return canvasLayer;
  } catch (err) {
    console.error('雷达目标Canvas图层初始化错误:', err);
    return null;
  }
}

/**
 * 更新雷达目标标记
 * @param {Object} canvasLayer CanvasMarker图层
 * @param {Array} radarTargets 雷达目标数据
 * @param {Object} radarIcon 雷达图标
 * @returns {Array} 创建的雷达目标标记数组
 */
export function updateRadarTargetMarkers(canvasLayer, radarTargets, radarIcon) {
  if (!canvasLayer || !Array.isArray(radarTargets) || !radarIcon) {
    console.error('无法更新雷达目标标记：缺少必要的组件');
    return [];
  }
  
  try {
    // console.log('更新雷达目标标记，传入雷达目标数量:', radarTargets.length);
    
    // 清空现有的标记
    try {
      canvasLayer.clearLayers();
    } catch (clearError) {
      console.warn('清空雷达目标Canvas图层失败:', clearError);
    }
    
    // 创建新的标记
    const radarMarkers = radarTargets
      .filter(target => target && target.pos && target.pos.latitude && target.pos.longitude)
      .map(target => {
        try {
          // 创建雷达目标标记 - 不旋转雷达图标
          const marker = L.marker(
            [target.pos.latitude, target.pos.longitude], 
            {
              icon: L.icon({
                iconUrl: radarIcon.options.iconUrl,
                iconSize: radarIcon.options.iconSize,
                iconAnchor: radarIcon.options.iconAnchor,
                className: 'radar-icon'
              })
            }
          );
          
          // 存储雷达目标数据到标记上
          marker.radarData = target;
          
          return marker;
        } catch (e) {
          console.error('创建雷达目标标记失败:', e, target);
          return null;
        }
      })
      .filter(Boolean); // 过滤掉创建失败的标记
    
    // 添加到Canvas图层
    if (radarMarkers.length > 0) {
      // console.log('添加雷达目标标记到Canvas图层，数量:', radarMarkers.length);
      try {
        canvasLayer.addLayers(radarMarkers);
        // 强制重绘Canvas图层
        if (canvasLayer._redraw) {
          canvasLayer._redraw(true);
        }
      } catch (addError) {
        console.error('添加雷达目标标记到Canvas图层失败:', addError);
      }
    } else {
      console.warn('没有有效的雷达目标标记可添加');
    }
    
    return radarMarkers;
  } catch (err) {
    console.error('更新雷达目标标记错误:', err);
    return [];
  }
} 